import React, { useEffect, useState } from 'react';
import { ThunderboltOutlined } from '@ant-design/icons';
import OfferBanner from '../common/OfferBanner';
import AutoApplyActivationModal from '../settings/AutoApplyActivationModal';
import { useAutoApplyGate } from '../../hooks/useAutoApplyGate';
import { captureEvent } from '../../services/analytics';
import { AutoApplyOfferSurface, UserSettings } from '../../types';

interface AutoApplyOfferCardProps {
  // Which page is hosting this card. Only affects analytics — the visibility
  // rules and dismissal persistence live in useAutoApplyOffer.
  surface: AutoApplyOfferSurface;
  // Current settings — the hook only reports visible when autoApplyEnabled is
  // false, so these seed the activation modal's fresh-setup defaults.
  settings: UserSettings;
  onSaved: (settings: UserSettings) => void;
  onDismiss: () => void;
}

/**
 * Dismissible Auto-Apply offer, shown above the table on Opportunities and on
 * Applications (design §10). Renders the shared OfferBanner — the site's
 * established "quiet offer above a table" pattern (also ReplyCaptureBanner) —
 * and opens the self-contained AutoApplyActivationModal, so the activation
 * flow itself exists in exactly one place regardless of host.
 *
 * Purely presentational: useAutoApplyOffer owns when it renders, dismissal
 * persistence, and the shared settings it saves back into.
 */
const AutoApplyOfferCard: React.FC<AutoApplyOfferCardProps> = ({
  surface,
  settings,
  onSaved,
  onDismiss,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  // Same gate as the Settings card. Without it a user fills in both steps here
  // and only meets the Gmail requirement on the final click.
  const { requestSetup, showGateAfterSaveError, probing, gate } = useAutoApplyGate(
    () => setModalOpen(true),
    surface
  );

  useEffect(() => {
    captureEvent('auto_apply_offer_shown', { surface });
  }, [surface]);

  const handleDismiss = () => {
    captureEvent('auto_apply_offer_dismissed', { surface });
    onDismiss();
  };

  const handleSetUp = () => {
    captureEvent('auto_apply_offer_cta_clicked', { surface });
    requestSetup();
  };

  return (
    <>
      <OfferBanner
        icon={<ThunderboltOutlined />}
        ctaLabel={probing ? 'Checking…' : 'Set up Auto-Apply'}
        onCta={handleSetUp}
        dismissAriaLabel="Dismiss Auto-Apply offer"
        onDismiss={handleDismiss}
      >
        Tired of clicking Apply? Auto-Apply sends your best matches for you — you stay in
        control with exclusions and a daily cap.
      </OfferBanner>

      <AutoApplyActivationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        settings={settings}
        onSaved={onSaved}
        onGmailRequired={showGateAfterSaveError}
      />

      {gate}
    </>
  );
};

export default AutoApplyOfferCard;
